<template>
  <div v-if="buttonText" class="cta">
    <EInstaller
      :visible="showOverlay"
      @installer-closed="closeInstaller"
    />
    <EButton
      :variant="variant"
      :class="{ visible: visible }"
      class="cta__button"
      data-test-id="cta-button"
      :data-track-id="trackId"
      :size="size"
      v-bind="ctaProps"
      @click="install"
    >
      <span
        v-if="icon"
        data-test-id="cta-button-icon"
        class="cta__icon"
        :class="`cta__icon--${icon}`"
      />
      {{ buttonText }}
    </EButton>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import {
  iconDesktopBrowsers,
  iconMobileBrowsers,
  showShareBrowsers,
} from '~~/common/js/constants/browsers';
import { FACEBOOK_SHARE_URL } from '~~/common/js/constants/urls';
import { getAppOrExtensionUrl } from '~~/common/js/universal/url-utils';
import { sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import EButton from '~~/common/vue/components/button';
import EInstaller from '~~/common/vue/components/installer';
import screen from '~~/common/vue/mixins/screen';

const enabledOverlay = ['firefox', 'ie'];

export default {
  name: 'ECta',
  components: {
    EButton,
    EInstaller,
  },
  mixins: [screen()],
  props: {
    customTrackId: {
      type: String,
      default: '',
    },
    customCtaText: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'l',
    },
    showShare: {
      type: Boolean,
      default: true,
    },
    showLogo: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: 'solid-green',
    },
  },
  data() {
    return {
      visible: false,
      showOverlay: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', [
      'addOn',
      'browser',
      'browserDisplay',
      'browserOs',
      'browserVersion',
    ]),
    buttonText() {
      if (this.disableInstall) {
        return this.showShare ? this.$t('common.header.counter.share') : '';
      }

      return this.customCtaText ?
        this.customCtaText :
        `${this.$t('common.install.cta')} ${this.browserDisplay}`;
    },
    icon() {
      const os = this.browserOs?.toLowerCase();
      const hasIconBrowser = iconDesktopBrowsers.includes(this.browser);
      const hasIconOs = iconMobileBrowsers.includes(os);
      const hasIcon = hasIconOs || hasIconBrowser;

      if (
        this.isTabletUp &&
        !this.disableInstall &&
        this.showLogo &&
        hasIcon
      ) {
        return hasIconOs ? os : this.browser;
      }

      return null;
    },
    appExtensionUrl() {
      return getAppOrExtensionUrl({
        os: this.browserOs || '',
        locale: this.locale,
        browser: this.browserDisplay || '',
        version: this.browserVersion,
      });
    },
    hrefAttr() {
      return this.disableInstall ?
        FACEBOOK_SHARE_URL :
        this.appExtensionUrl;
    },
    disableInstall() {
      return this.addOn || showShareBrowsers.includes(this.browser);
    },
    trackId() {
      let trackEvent = this.disableInstall ? 'share_cta' : 'install_cta';
      if (this.customTrackId) {
        trackEvent = `${this.customTrackId}_${trackEvent}`;
      }

      return trackEvent;
    },
    ctaProps() {
      const target = enabledOverlay.includes(this.browser) ?
        null :
        '_blank';

      if (this.browser === 'ie') {
        return {};
      }

      return {
        href: this.hrefAttr,
        as: 'a',
        target,
        rel: 'noopener',
      };
    },
  },
  mounted() {
    this.visible = true;

    if (this.buttonText) {
      sendCoreAnalyticsEvent('ctaDisplayed', {
        name: this.trackId,
      });
    }
  },
  methods: {
    closeInstaller() {
      this.showOverlay = false;
    },
    install() {
      sendCoreAnalyticsEvent('ctaClicked', {
        name: this.trackId,
      });

      // The installer function is needed to trigger an
      // overlay for firefox and internet explorer
      if (this.disableInstall ||
          !enabledOverlay.includes(this.browser)) {
        return;
      }

      if (this.browser === 'ie') {
        if (window.external && ('AddSearchProvider' in window.external)) {
          window.external.AddSearchProvider(this.hrefAttr);
        }
      }

      this.showOverlay = true;
    },
  },
};
</script>

<style lang="scss" scoped>
$install-icons: (
  android,
  chrome,
  edge,
  firefox,
  ios,
  safari,
);

.cta {
  display: inline-block;
}

.cta__button {
  @include class-opacity-transition;

  /* Needed to align the browser icons */
  ::v-deep .icon {
    margin-right: $space-2s;
    margin-top: -$space-2s/2;
  }
}

.cta__icon {
  width: $icon-size-m;
  height: $icon-size-m;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: $space-2s;
  margin-bottom: $space-2s;
}

@each $icon in $install-icons {
  .cta__icon--#{$icon} {
    background-image: url(~~/common/vue/assets/cta-icons/#{$icon}.svg?asPath);
  }
}
</style>
